import { useState, useMemo } from 'react';
import { useAnimationStore } from '../stores/animationStore';
import { ColorPalette, CursorEffectType } from '../types';
import {
  SettingOutlined,
  SwapOutlined,
  ThunderboltOutlined,
  BgColorsOutlined,
  AimOutlined,
  BorderOutlined,
  ReloadOutlined,
  FireOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { generateConfigHash } from '../utils/configHash';
import {
  SettingsTab,
  getAvailableTabs,
  getVisibleSettings,
  isSettingVisible,
  basicSettings,
  movementSettings,
  waveSettings,
  colorsSettings,
  depthSettings,
  cursorSettings,
  fireflySettings,
  buttonSettings,
} from '../utils/configApplicability';

const backgrounds: { value: 'dark' | 'light' | 'transparent' | 'gradient'; label: string }[] = [
  { value: 'dark', label: 'Тёмный' },
  { value: 'light', label: 'Светлый' },
  { value: 'transparent', label: 'Прозрачный' },
  { value: 'gradient', label: 'Градиент' },
];

interface Tab {
  id: SettingsTab;
  icon: React.ReactNode;
  title: string;
}

const tabIcons: Record<SettingsTab, React.ReactNode> = {
  basic: <SettingOutlined />,
  movement: <SwapOutlined />,
  wave: <ThunderboltOutlined />,
  colors: <BgColorsOutlined />,
  depth: <AimOutlined />,
  cursor: <BorderOutlined />,
  firefly: <FireOutlined />,
  button: <AppstoreOutlined />,
};

const tabLabels: Record<SettingsTab, string> = {
  basic: 'Основные',
  movement: 'Движение',
  wave: 'Волна',
  colors: 'Цвета',
  depth: 'Глубина',
  cursor: 'Курсор',
  firefly: 'Firefly',
  button: 'Кнопка',
};

const colorPalettes: { value: string; label: string; colors: string[] }[] = [
  { value: 'default', label: 'По умолчанию', colors: ['#ffffff'] },
  {
    value: 'fire',
    label: 'Огонь',
    colors: ['#ff4500', '#ff6600', '#ff8800', '#ffaa00', '#ffcc00'],
  },
  { value: 'ocean', label: 'Океан', colors: ['#006994', '#0077be', '#00bfff', '#40e0d0'] },
  { value: 'forest', label: 'Лес', colors: ['#228b22', '#006400', '#32cd32', '#90ee90'] },
  { value: 'neon', label: 'Неон', colors: ['#ff00ff', '#00ffff', '#ff0080', '#8000ff'] },
  {
    value: 'rainbow',
    label: 'Радуга',
    colors: ['#ff0000', '#ff7700', '#ffff00', '#00ff00', '#0000ff', '#8b00ff'],
  },
  { value: 'sunset', label: 'Закат', colors: ['#ff6b6b', '#feca57', '#ff9ff3', '#54a0ff'] },
];

const SliderControl = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format = (v: number) => v.toString(),
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <label style={{ color: '#888', fontSize: 13 }}>
      {label}: {format(value)}
    </label>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      style={{ width: '100%', cursor: 'pointer' }}
    />
  </div>
);

// Generic settings renderer
const renderSettingsByKeys = (
  settingKeys: string[],
  config: any,
  category: string,
  subType: string,
  updateConfig: (cfg: Record<string, unknown>) => void
) => {
  return settingKeys.map((key) => {
    if (!isSettingVisible(key as any, category as any, subType as any)) {
      return null;
    }

    const setting = [
      ...basicSettings,
      ...movementSettings,
      ...waveSettings,
      ...colorsSettings,
      ...depthSettings,
      ...cursorSettings,
      ...fireflySettings,
      ...buttonSettings,
    ].find((s) => s.key === key);
    if (!setting) return null;

    switch (setting.type) {
      case 'slider':
        const numValue = config[key] ?? setting.min ?? 0;
        return (
          <SliderControl
            key={key}
            label={setting.label}
            value={numValue}
            min={setting.min ?? 0}
            max={setting.max ?? 100}
            step={setting.step ?? 1}
            onChange={(v) => updateConfig({ [key]: v })}
            format={setting.format}
          />
        );
      case 'toggle':
        return (
          <div
            key={key}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <label style={{ color: '#888', fontSize: 13 }}>{setting.label}</label>
            <button
              onClick={() => updateConfig({ [key]: !config[key] })}
              style={{
                background: config[key] ? '#27ae60' : '#2a2a2a',
                border: '1px solid #333',
                borderRadius: 6,
                padding: '6px 12px',
                color: '#fff',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {config[key] ? 'Включено' : 'Выключено'}
            </button>
          </div>
        );
      case 'select':
        return (
          <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ color: '#888', fontSize: 13 }}>{setting.label}</label>
            <select
              value={config[key] || ''}
              onChange={(e) => updateConfig({ [key]: e.target.value })}
              style={{
                background: '#2a2a2a',
                border: '1px solid #333',
                borderRadius: 6,
                padding: '8px 12px',
                color: '#fff',
                fontSize: 14,
              }}
            >
              {setting.options?.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        );
      case 'color':
        return (
          <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ color: '#888', fontSize: 13 }}>{setting.label}</label>
            <input
              type="color"
              value={config[key] || '#ffffff'}
              onChange={(e) => updateConfig({ [key]: e.target.value })}
              style={{
                width: '100%',
                height: 40,
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            />
            {setting.description && (
              <label style={{ color: '#666', fontSize: 11 }}>{setting.description}</label>
            )}
          </div>
        );
      default:
        return null;
    }
  });
};

export const AnimationControls = () => {
  const {
    currentPreset,
    updateConfig,
    setPlaying,
    isPlaying,
    previewBackground,
    setPreviewBackground,
    resetConfig,
  } = useAnimationStore();
  const [activeTab, setActiveTab] = useState<SettingsTab>('basic');

  if (!currentPreset) return null;

  const config = currentPreset.config;
  const category = currentPreset.category;
  const subType = currentPreset.subType;

  // Get available tabs based on category
  const availableTabs = useMemo(() => getAvailableTabs(category), [category]);

  // Reset active tab if not available
  useMemo(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0] || 'basic');
    }
  }, [availableTabs]);

  const handleConfigChange = (newConfig: Record<string, unknown>) => {
    updateConfig(newConfig);
  };

  const handleNumberChange = (key: string, value: number) => {
    updateConfig({ [key]: value });
  };

  const handleColorChange = (value: string) => {
    updateConfig({ color: value });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ color: '#888', fontSize: 13 }}>Название</label>
              <input
                type="text"
                value={currentPreset.name}
                onChange={(e) =>
                  useAnimationStore.setState((state) => ({
                    currentPreset: state.currentPreset
                      ? {
                          ...state.currentPreset,
                          name: e.target.value,
                          updatedAt: new Date().toISOString(),
                        }
                      : null,
                  }))
                }
                style={{
                  background: '#2a2a2a',
                  border: '1px solid #333',
                  borderRadius: 6,
                  padding: '8px 12px',
                  color: '#fff',
                  fontSize: 14,
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ color: '#888', fontSize: 13 }}>Категория</label>
              <div
                style={{
                  background: '#2a2a2a',
                  border: '1px solid #333',
                  borderRadius: 6,
                  padding: '8px 12px',
                  color: '#fff',
                  fontSize: 14,
                }}
              >
                {currentPreset.category}
              </div>
            </div>

            <SliderControl
              label="Скорость"
              value={config.speed}
              min={0.1}
              max={10}
              step={0.1}
              onChange={(v) => handleNumberChange('speed', v)}
              format={(v) => v.toFixed(1)}
            />

            <SliderControl
              label="Интенсивность"
              value={config.intensity}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => handleNumberChange('intensity', v)}
              format={(v) => v.toFixed(2)}
            />

            <SliderControl
              label="Количество"
              value={config.count || 100}
              min={10}
              max={500}
              step={10}
              onChange={(v) => handleNumberChange('count', v)}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ color: '#888', fontSize: 13 }}>Масштабировать под размер</label>
              <button
                onClick={() => updateConfig({ scaleCount: !config.scaleCount })}
                style={{
                  background: config.scaleCount ? '#3498db' : '#2a2a2a',
                  border: '1px solid #333',
                  borderRadius: 6,
                  padding: '8px 12px',
                  color: '#fff',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {config.scaleCount ? 'Включено' : 'Выключено'}
              </button>
              <label style={{ color: '#666', fontSize: 11 }}>
                Количество частиц подстраивается под размер элемента
              </label>
            </div>

            <SliderControl
              label="Размер"
              value={config.size || 3}
              min={1}
              max={30}
              step={1}
              onChange={(v) => handleNumberChange('size', v)}
            />

            <SliderControl
              label="Прозрачность"
              value={config.opacity}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => handleNumberChange('opacity', v)}
              format={(v) => v.toFixed(2)}
            />
          </>
        );

      case 'movement':
        return (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ color: '#888', fontSize: 13 }}>Направление</label>
              <select
                value={config.direction || 'down'}
                onChange={(e) => updateConfig({ direction: e.target.value as any })}
                style={{
                  background: '#2a2a2a',
                  border: '1px solid #333',
                  borderRadius: 6,
                  padding: '8px 12px',
                  color: '#fff',
                  fontSize: 14,
                }}
              >
                <option value="down">Вниз</option>
                <option value="up">Вверх</option>
                <option value="left">Влево</option>
                <option value="right">Вправо</option>
                <option value="random">Случайно</option>
              </select>
            </div>

            <SliderControl
              label="Ветер (горизонтально)"
              value={config.wind || 0}
              min={-5}
              max={5}
              step={0.1}
              onChange={(v) => handleNumberChange('wind', v)}
              format={(v) => v.toFixed(1)}
            />

            <SliderControl
              label="Гравитация"
              value={config.gravity || 0}
              min={0}
              max={2}
              step={0.05}
              onChange={(v) => handleNumberChange('gravity', v)}
              format={(v) => v.toFixed(2)}
            />

            <div
              style={{
                padding: 12,
                background: '#252525',
                borderRadius: 8,
                fontSize: 12,
                color: '#666',
              }}
            >
              Направление — базовое направление движения частиц. Ветер добавляет горизонтальное
              смещение. Гравитация — ускорение в направлении движения.
            </div>
          </>
        );

      case 'wave':
        return (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ color: '#888', fontSize: 13 }}>Включить волну</label>
              <button
                onClick={() => updateConfig({ waveEnabled: !config.waveEnabled })}
                style={{
                  background: config.waveEnabled ? '#27ae60' : '#2a2a2a',
                  border: '1px solid #333',
                  borderRadius: 6,
                  padding: '6px 12px',
                  color: '#fff',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {config.waveEnabled ? 'Включено' : 'Выключено'}
              </button>
            </div>

            {config.waveEnabled && (
              <>
                <SliderControl
                  label="Амплитуда"
                  value={config.waveAmplitude || 10}
                  min={1}
                  max={100}
                  step={1}
                  onChange={(v) => handleNumberChange('waveAmplitude', v)}
                />

                <SliderControl
                  label="Частота"
                  value={config.waveFrequency || 0.02}
                  min={0.001}
                  max={0.1}
                  step={0.001}
                  onChange={(v) => handleNumberChange('waveFrequency', v)}
                  format={(v) => v.toFixed(3)}
                />

                <SliderControl
                  label="Скорость волны"
                  value={config.waveSpeed || 1}
                  min={0.1}
                  max={5}
                  step={0.1}
                  onChange={(v) => handleNumberChange('waveSpeed', v)}
                  format={(v) => v.toFixed(1)}
                />
              </>
            )}

            <div
              style={{
                padding: 12,
                background: '#252525',
                borderRadius: 8,
                fontSize: 12,
                color: '#666',
              }}
            >
              Волна добавляет синусоидальное смещение по горизонтали. Амплитуда — размах, частота —
              плотность волн.
            </div>
          </>
        );

      case 'colors':
        return (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ color: '#888', fontSize: 13 }}>Цветовая палитра</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {colorPalettes.map((palette) => (
                  <button
                    key={palette.value}
                    onClick={() => updateConfig({ colorPalette: palette.value as ColorPalette })}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: config.colorPalette === palette.value ? '#3498db' : '#2a2a2a',
                      border: '1px solid #333',
                      borderRadius: 6,
                      padding: '8px 12px',
                      color: '#fff',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: 2,
                        width: 60,
                        height: 16,
                        borderRadius: 3,
                        overflow: 'hidden',
                      }}
                    >
                      {palette.colors.map((c, i) => (
                        <div key={i} style={{ flex: 1, background: c }} />
                      ))}
                    </div>
                    <span>{palette.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ color: '#888', fontSize: 13 }}>Свой цвет</label>
              <input
                type="color"
                value={Array.isArray(config.color) ? config.color[0] : config.color}
                onChange={(e) => handleColorChange(e.target.value)}
                style={{
                  width: '100%',
                  height: 40,
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              />
              <label style={{ color: '#666', fontSize: 11 }}>Переопределяет палитру</label>
            </div>
          </>
        );

      case 'depth':
        return (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ color: '#888', fontSize: 13 }}>Включить глубину</label>
              <button
                onClick={() => updateConfig({ depthEnabled: !config.depthEnabled })}
                style={{
                  background: config.depthEnabled ? '#3498db' : '#2a2a2a',
                  border: '1px solid #333',
                  borderRadius: 6,
                  padding: '10px 16px',
                  color: '#fff',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {config.depthEnabled ? 'Включено' : 'Выключено'}
              </button>
            </div>

            {config.depthEnabled && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ color: '#888', fontSize: 13 }}>
                    Количество слоёв: {config.depthLayers || 3}
                  </label>
                  <input
                    type="range"
                    min={2}
                    max={5}
                    step={1}
                    value={config.depthLayers || 3}
                    onChange={(e) => updateConfig({ depthLayers: parseInt(e.target.value) })}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ color: '#888', fontSize: 13 }}>
                    Фокусное расстояние: {Math.round((config.depthFocus || 0.5) * 100)}%
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.1}
                    value={config.depthFocus || 0.5}
                    onChange={(e) => updateConfig({ depthFocus: parseFloat(e.target.value) })}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                  <label style={{ color: '#666', fontSize: 11 }}>
                    Ближе к 0 - ближний план, ближе к 1 - дальний план
                  </label>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ color: '#888', fontSize: 13 }}>
                    Размытие (потребляет ресурсы)
                  </label>
                  <button
                    onClick={() => updateConfig({ depthBlur: !config.depthBlur })}
                    style={{
                      background: config.depthBlur ? '#3498db' : '#2a2a2a',
                      border: '1px solid #333',
                      borderRadius: 6,
                      padding: '8px 12px',
                      color: '#fff',
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    {config.depthBlur ? 'Включено' : 'Выключено'}
                  </button>
                  <label style={{ color: '#666', fontSize: 11 }}>
                    Добавляет эффект размытия для дальних слоёв
                  </label>
                </div>
              </>
            )}
          </>
        );

      case 'cursor':
        return (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ color: '#888', fontSize: 13 }}>Эффект курсора</label>
              <select
                value={config.cursorEffect || 'none'}
                onChange={(e) => updateConfig({ cursorEffect: e.target.value as CursorEffectType })}
                style={{
                  background: '#2a2a2a',
                  border: '1px solid #333',
                  borderRadius: 6,
                  padding: '8px 12px',
                  color: '#fff',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <option value="none">Нет</option>
                <option value="fear">Страх (убегают)</option>
                <option value="attract">Притягивание</option>
                <option value="stop">Остановка</option>
                <option value="avoid">Избегание</option>
                <option value="fade">Исчезновение</option>
                <option value="slow">Замедление</option>
              </select>
            </div>

            {config.cursorEffect && config.cursorEffect !== 'none' && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ color: '#888', fontSize: 13 }}>
                    Радиус действия: {config.cursorRadius || 100}px
                  </label>
                  <input
                    type="range"
                    min={30}
                    max={300}
                    step={10}
                    value={config.cursorRadius || 100}
                    onChange={(e) => updateConfig({ cursorRadius: parseInt(e.target.value) })}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ color: '#888', fontSize: 13 }}>
                    Сила эффекта: {config.cursorStrength || 1}
                  </label>
                  <input
                    type="range"
                    min={0.1}
                    max={3}
                    step={0.1}
                    value={config.cursorStrength || 1}
                    onChange={(e) => updateConfig({ cursorStrength: parseFloat(e.target.value) })}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ color: '#888', fontSize: 13 }}>
                    Инерция: {Math.round((config.cursorInertia ?? 0.95) * 100)}%
                  </label>
                  <input
                    type="range"
                    min={0.8}
                    max={1}
                    step={0.01}
                    value={config.cursorInertia ?? 0.95}
                    onChange={(e) => updateConfig({ cursorInertia: parseFloat(e.target.value) })}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                  <label style={{ color: '#666', fontSize: 11 }}>
                    Ниже - меньше инерции, выше - плавнее затухание
                  </label>
                </div>
              </>
            )}
          </>
        );

      case 'firefly':
        return (
          <>
            <div
              style={{
                padding: 12,
                background: '#252525',
                borderRadius: 8,
                fontSize: 12,
                color: '#888',
                marginBottom: 8,
              }}
            >
              Мерцающий огонёк бежит по контуру кнопки как светлячок
            </div>

            <SliderControl
              label="Размер огонька"
              value={config.fireflySize || 6}
              min={2}
              max={20}
              step={1}
              onChange={(v) => handleNumberChange('fireflySize', v)}
            />

            <SliderControl
              label="Свечение"
              value={config.fireflyGlow || 15}
              min={0}
              max={50}
              step={1}
              onChange={(v) => handleNumberChange('fireflyGlow', v)}
            />

            <SliderControl
              label="Скорость"
              value={config.fireflySpeed || 1}
              min={0.1}
              max={5}
              step={0.1}
              onChange={(v) => handleNumberChange('fireflySpeed', v)}
              format={(v) => v.toFixed(1)}
            />

            <SliderControl
              label="Мерцание"
              value={config.fireflyBlink ?? 0.8}
              min={0}
              max={1}
              step={0.05}
              onChange={(v) => handleNumberChange('fireflyBlink', v)}
              format={(v) => v.toFixed(2)}
            />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ color: '#888', fontSize: 13 }}>Шлейф</label>
              <button
                onClick={() => updateConfig({ fireflyTrail: config.fireflyTrail ? 0 : 1 })}
                style={{
                  background: config.fireflyTrail ? '#27ae60' : '#2a2a2a',
                  border: '1px solid #333',
                  borderRadius: 6,
                  padding: '6px 12px',
                  color: '#fff',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {config.fireflyTrail ? 'Включено' : 'Выключено'}
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ color: '#888', fontSize: 13 }}>Размытие свечения</label>
              <button
                onClick={() => updateConfig({ fireflyBlur: config.fireflyBlur ? 0 : 1 })}
                style={{
                  background: config.fireflyBlur ? '#27ae60' : '#2a2a2a',
                  border: '1px solid #333',
                  borderRadius: 6,
                  padding: '6px 12px',
                  color: '#fff',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {config.fireflyBlur ? 'Включено' : 'Выключено'}
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ color: '#888', fontSize: 13 }}>Цвет огонька</label>
              <input
                type="color"
                value={config.fireflyColor || '#00ffaa'}
                onChange={(e) => updateConfig({ fireflyColor: e.target.value })}
                style={{
                  width: '100%',
                  height: 40,
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              />
            </div>
          </>
        );

      case 'button':
        return (
          <>
            <SliderControl
              label="Толщина бордера"
              value={config.borderWidth || 2}
              min={1}
              max={10}
              step={1}
              onChange={(v) => updateConfig({ borderWidth: v })}
            />

            <SliderControl
              label="Скругление углов"
              value={config.borderRadius || 8}
              min={0}
              max={30}
              step={1}
              onChange={(v) => updateConfig({ borderRadius: v })}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        background: '#1a1a1a',
        borderRadius: 12,
        padding: 16,
        width: 300,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h3 style={{ color: '#fff', margin: 0 }}>Настройки</h3>
          {currentPreset && (
            <span
              title="Хеш конфигурации"
              style={{
                background: '#333',
                padding: '2px 6px',
                borderRadius: 4,
                fontSize: 10,
                color: '#888',
                fontFamily: 'monospace',
              }}
            >
              {generateConfigHash(currentPreset.config)}
            </span>
          )}
        </div>
        <button
          onClick={resetConfig}
          title="Сбросить настройки"
          style={{
            background: 'transparent',
            border: '1px solid #333',
            borderRadius: 4,
            padding: '4px 8px',
            color: '#666',
            fontSize: 14,
            cursor: 'pointer',
            lineHeight: 1,
          }}
        >
          <ReloadOutlined />
        </button>
      </div>

      {/* Tabs - dynamically based on category */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid #333',
          paddingBottom: 8,
        }}
      >
        {availableTabs.map((tabId) => (
          <button
            key={tabId}
            onClick={() => setActiveTab(tabId)}
            title={tabLabels[tabId]}
            style={{
              flex: 1,
              background: activeTab === tabId ? '#3498db' : 'transparent',
              border: 'none',
              borderRadius: 6,
              padding: '8px 4px',
              color: '#fff',
              fontSize: 16,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {tabIcons[tabId]}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          flex: 1,
          overflow: 'auto',
        }}
      >
        {renderTabContent()}
      </div>

      {/* Play button */}
      <button
        onClick={() => setPlaying(!isPlaying)}
        style={{
          background: isPlaying ? '#e74c3c' : '#27ae60',
          border: 'none',
          borderRadius: 6,
          padding: '12px 16px',
          color: '#fff',
          fontSize: 14,
          cursor: 'pointer',
          fontWeight: 600,
          marginTop: 'auto',
        }}
      >
        {isPlaying ? 'Пауза' : 'Воспроизвести'}
      </button>
    </div>
  );
};
